import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LogOut,
  Moon,
  Sun,
  Languages,
  Menu,
  BookOpen,
  Play,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { setLanguage } from "@/i18n";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import LogoMonogram from "@/components/LogoMonogram";
import ShareAppButton from "@/components/ShareAppButton";

/**
 * Topbar — sticky header with the mobile-sidebar toggle, brand mark and the
 * dropdowns (language, theme, share-app, help, user menu).
 */
export default function Topbar({ onToggleMobile }) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-background/95 px-4 backdrop-blur sm:px-6">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onToggleMobile}
        data-testid="toggle-sidebar-btn"
      >
        <Menu className="h-4 w-4" />
      </Button>

      {/* Brand mark — visible in mobile too because the sidebar collapses. */}
      <div className="flex items-center gap-2" data-testid="topbar-brand">
        <LogoMonogram size={28} testId="topbar-logo" />
        <span className="hidden text-sm font-semibold tracking-tight sm:inline">
          {t("app.name")}
        </span>
      </div>

      <div className="flex-1" />

      {/* Language */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" data-testid="lang-toggle" className="gap-2">
            <Languages className="h-4 w-4" />
            <span className="font-mono text-xs uppercase">{i18n.language}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem data-testid="lang-es" onClick={() => setLanguage("es")}>
            Español
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="lang-en" onClick={() => setLanguage("en")}>
            English
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Theme */}
      <Button
        variant="outline"
        size="icon"
        onClick={toggle}
        data-testid="theme-toggle"
        title={theme === "dark" ? "Light" : "Dark"}
      >
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>

      {/* Share the app */}
      <ShareAppButton variant="icon" testId="topbar-share-app" />

      {/* Quick help — direct shortcut to /help and a way to re-trigger the
          onboarding tour from anywhere in the app. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            data-testid="help-menu-trigger"
            title={t("nav.help")}
          >
            <BookOpen className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-48">
          <DropdownMenuItem data-testid="help-menu-open" onClick={() => navigate("/help")}>
            <BookOpen className="mr-2 h-4 w-4" /> {t("nav.help")}
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="help-menu-replay-tour"
            onClick={() => window.dispatchEvent(new Event("muleria:restart-tour"))}
          >
            <Play className="mr-2 h-4 w-4" /> {t("onboarding.replay")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-trigger">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="hidden text-left leading-tight sm:block">
              <div className="text-sm font-medium" data-testid="user-name">
                {user?.name || user?.email}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {user?.role}
              </div>
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-48">
          <DropdownMenuLabel>
            <div className="truncate">{user?.email}</div>
            <Badge variant="outline" className="mt-1 text-[10px]">
              {user?.role}
            </Badge>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onLogout} data-testid="logout-btn">
            <LogOut className="mr-2 h-4 w-4" /> {t("nav.logout")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
